import React, { useCallback, useContext, useRef, useState } from "react";
import PortableText from "./portableText";
import { cn, toPlainText } from "../lib/helpers";
import { Byline } from "./card/byline";
import getYouTubeId from "get-youtube-id";
import { Podcast, SectionWrapper } from "./sections";
import { ShareWidgets } from "./share-widgets";
import { pageDocPath } from "../../helpers";
import { CardGrid } from "./card/card-grid";
import { SiteContext } from "./global/site-context";
import { BackToZone } from "./back-to-zone";
import { Pills } from "./card/pills";
import { PublishDate } from "./card/publish-date";
import useMedia from "use-media";
import { Promo } from "./sections/promo";
import { Button } from "./button";
import { ArrowLeft } from "./icon/arrow-left";
import { ImageBuilder } from "./global/image-builder";
import { PodcastLinks } from "./sections/snowflake/podcast";
import { ProductDisclaimer } from "./product-disclaimer";
import { CardContext } from "./card/context";
import { CallToAction } from "./serializers-cta";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPauseCircle, faPlayCircle } from "@fortawesome/free-solid-svg-icons";

import * as styles from "./post-page.module.css";

const HERO_IMAGE_WIDTH = 640;
const PAGE_CONTAINER = "narrow";

const PODCAST_RELATED_TAGS = [
  "17e46daf-7cde-44a3-b8b3-7ffcf6c7b86b",
  "da38ee48-4ccb-4632-aed4-fae6aeb77b8c",
  "e398c056-1d60-450c-9156-6f34a1808f89",
  "62c8d09b-665d-4ecb-bc7b-e6769e14963a",
];

const PODCAST_YT_LIST_ID = "PLKDS0si7azFltUVuYOUW1aby9NVevUsA1";

export const BlogPostPage = ({
  title,
  authors,
  _rawBody,
  _rawExcerpt,
  mainImage,
  mainVideo,
  cardImage,
  hideMainMedia,
  slug,
  suggest,
  categories,
  readingTimeInMinutes,
  promo,
  mainAudio,
  duration,
  recentPosts,
  _createdAt,
  publishedAt
}) => {
  const site = useContext(SiteContext);
  const noMedia = hideMainMedia || (!mainImage && !mainVideo);
  const hasVideo = !hideMainMedia && mainVideo && mainVideo.url;
  const isLarge = useMedia({ minWidth: "1024px" });
  const podcastRelated =
    categories.some((c) => PODCAST_RELATED_TAGS.includes(c._id));

  const Share = useCallback(
    () => (
      <ShareWidgets
        title={title}
        excerpt={toPlainText(_rawExcerpt)}
        path={pageDocPath({ _type: "post", slug })}
        vertical={isLarge}
        size={24}
      />
    ),
    [title, _rawExcerpt, slug, site, isLarge]
  );

  // @todo Podcast embed

  return (
    <>
      <div className="z-40 left-0 top-0 xl:pl-8 lg:fixed">
        <SectionWrapper padding="none" container="lg">
          <div className="mb-32 hidden lg:block"></div>
          {categories.find(
            (c) => c._id === "17e46daf-7cde-44a3-b8b3-7ffcf6c7b86b"
          ) && (
              <div className="mb-6">
                <Button
                  text="Explore series"
                  href="/podcast/"
                  icon={<ArrowLeft size="12" />}
                  bgColor="bg-dark-10"
                />
              </div>
            )}

          <BackToZone to="/blog/" text={site.token("backToBlog")} />

          <h3 className="text-sm mb-4">{readingTimeInMinutes} min read</h3>
          {isLarge && (
            <div className="mb-12">
              <Share />
            </div>
          )}
        </SectionWrapper>
      </div>

      <SectionWrapper container={PAGE_CONTAINER} padding="top">
        <div className="z-20 relative">
          {hasVideo && (
            <div className="rounded-xl overflow-hidden" style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src={`https://www.youtube.com/embed/${getYouTubeId(mainVideo.url)}?controls=0&rel=0`}
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
              />
            </div>
          )}
          {!hideMainMedia &&
            !mainVideo &&
            (mainImage?.asset || cardImage?.assset) && (
              <div className="block relative rounded-2xl overflow-hidden">
                <ImageBuilder
                  image={cardImage || mainImage}
                  width={HERO_IMAGE_WIDTH}
                  height={
                    mainImage?.asset
                      ? Math.round(
                        HERO_IMAGE_WIDTH /
                        mainImage.asset?.metadata?.dimensions?.aspectRatio
                      )
                      : null
                  }
                  alt={cardImage ? title : mainImage.alt}
                />
              </div>
            )}
        </div>

        {noMedia && <h1 className="text-5xl mt-2">{title}</h1>}

        <div className="flex gap-1 items-center mt-4 mb-2">
          <CardContext.Provider value={{ hideType: true }}>
            {categories && <Pills tags={categories} showSubTags={true} />}
          </CardContext.Provider>
          <div className="inline-block ml-1 text-sm text-gray-600">
            <PublishDate />
          </div>
        </div>

        {!noMedia && (
          <h1 className="text-5xl mt-2">
            {`${title}${hasVideo ? " [video]" : ""}`}
          </h1>
        )}

        {!podcastRelated && mainAudio?.asset?.url && (
          <ListenAudio mainAudio={mainAudio} title={title} />
        )}

        {podcastRelated && (
          <PodcastLinks
            className="p-2 mt-4 border border-dark-10 rounded-xl bg-dark-2"
            hideYouTube={true}
          />
        )}

        {!isLarge && (
          <div className="mt-6">
            <Share />
          </div>
        )}

        <ProductDisclaimer
          categories={categories}
          _createdAt={_createdAt}
          publishedAt={publishedAt}
        />
      </SectionWrapper>

      {/* {companies && companies.map((c) => <p>{c.title}</p>)} */}

      <SectionWrapper container={PAGE_CONTAINER} padding="tight">
        {authors.length > 0 && (
          <div className="pt-3 pb-6 md:pt-0">
            <Byline />
          </div>
        )}
        <div className={cn("leading-relaxed", styles.mainContent)} id="article">
          {_rawBody && <PortableText blocks={_rawBody} />}
        </div>
      </SectionWrapper>

      {promo && (
        <SectionWrapper container={PAGE_CONTAINER} padding="tight">
          <Promo {...promo} />
        </SectionWrapper>
      )}

      <SectionWrapper container={PAGE_CONTAINER} padding="tight">
        <CallToAction action="bookDemo" />
      </SectionWrapper>

      {site.isActive("suggestions") && suggest && (
        <SectionWrapper
          header={podcastRelated ? "Listen to the podcast" : site.token("suggestions")}
          anchor="more"
          container={PAGE_CONTAINER}
          background="lavenderUltraLight"
        >
          {podcastRelated ? (
            <div className="mb-8">
              <Podcast />
            </div>
          ) : (
            <CardGrid
              nodes={suggest.concat(recentPosts || []).slice(0, 4)}
              browseMoreHref="/blog/"
              maxColumns={2}
              allowSummary={true}
              showSummary={true}
              panels={true}
              attribution={{ action: "suggestion", label: "post" }}
              makeThumbnail={true}
            />
          )}
        </SectionWrapper>
      )}
    </>

  );
};

export const ListenAudio = ({ mainAudio, title }) => {
  const site = useContext(SiteContext);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const track = (action) =>
    site.metrics.logEvent({ category: "audio", action, label: title });

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play();
    else audio.pause();
  };

  return (
    <div className="mt-6">
      <button
        type="button"
        onClick={toggle}
        aria-pressed={playing}
        className="group inline-flex items-center gap-2 pl-2 pr-4 py-2 rounded-full border border-dark-10 bg-dark-2 font-semibold transition-colors hover:bg-dark-10 hover:border-link"
      >
        <FontAwesomeIcon
          icon={playing ? faPauseCircle : faPlayCircle}
          className="text-3xl text-link transition-transform group-hover:scale-110"
        />
        {playing ? "Pause" : "Listen"}
      </button>
      <audio
        ref={audioRef}
        src={mainAudio.asset.url}
        preload="metadata"
        onPlay={() => {
          setPlaying(true);
          track("play");
        }}
        onPause={(e) => {
          setPlaying(false);
          if (!e.currentTarget.ended) track("pause");
        }}
        onEnded={() => {
          setPlaying(false);
          track("complete");
        }}
      />
    </div>
  );
};